import React, { useMemo } from 'react';
import { Icon, ActionButton, IIconStyles, getTheme, IContextualMenuProps, CommandButton } from '@fluentui/react';
import { usePRIVATE_STYLES } from './styles';
import * as formService from '../../form-services/presentation-editor';
import { AppRoutes } from '../../../app-routes';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import { secondsToDurationString } from '../../../utils';
import { PRIVATE_PANEL_DISPLAY_BUTTON } from './panel-button';
import { DurationField } from '../../../../components/duration-field';
import { PreviewLayoutMode } from '../../entities/preview-layout-mode';

xlationContext('Presentation editor header');

export interface IPresentationEditorHeaderProps {
  onSavePresentation: () => void;
  onStartPresentation: (() => void) | undefined;
  onOpenPreviewPanel: () => void;
  onDisplayPreview: () => void;
  onDisplayHelp: () => void;
  saveDisabled: boolean;
  showDisplayPanelButton: boolean;
  allowStartPresentationButton: boolean;
  showDisplayPreviewHelpPanelButton: boolean;
  userLayoutMode: PreviewLayoutMode | undefined;
  onSetUserLayoutMode: (mode: PreviewLayoutMode | undefined) => void;
}

const onDurationFieldUpdate = (value: number | null | undefined) => formService.presenterDefinedDuration.onUpdate(value ?? undefined);

const getLayoutMenuProps = (currentMode: PreviewLayoutMode | undefined, action: (mode: PreviewLayoutMode | undefined) => void) => {
  const layoutMenuProps: IContextualMenuProps = {
    items: [
      {
        key: 'auto',
        text: xlate('Automatic'),
        iconProps: { iconName: 'TriggerAuto' },
        canCheck: true,
        checked: currentMode === undefined,
        onClick: () => action(undefined),
      }, {
        key: 'vertical',
        text: xlate('Vertical'),
        iconProps: { iconName: 'DoubleColumn' },
        canCheck: true,
        checked: currentMode === PreviewLayoutMode.Vertical,
        onClick: () => action(PreviewLayoutMode.Vertical),
      }, {
        key: 'horizontal',
        text: xlate('Horizontal'),
        iconProps: { iconName: 'AutoFillTemplate' },
        canCheck: true,
        checked: currentMode === PreviewLayoutMode.Horizontal,
        onClick: () => action(PreviewLayoutMode.Horizontal),
      }, {
        key: 'panel',
        text: xlate('Side Panel'),
        iconProps: { iconName: 'OpenPane' },
        canCheck: true,
        checked: currentMode === PreviewLayoutMode.Paneled,
        onClick: () => action(PreviewLayoutMode.Paneled),
      },
    ],
  };

  return layoutMenuProps;
}

const subscriberId = 'Component.PresentationEditorHeader';

export const PresentationEditorHeader: React.FunctionComponent<IPresentationEditorHeaderProps> = (props) => {
  const { userLayoutMode, onSetUserLayoutMode } = props;
  const theme = getTheme();
  const formModel = formService.useFormModel(subscriberId);

  const layoutMenuProps = useMemo(() => {
    return getLayoutMenuProps(userLayoutMode, onSetUserLayoutMode);
  }, [userLayoutMode, onSetUserLayoutMode]);

  const styles = usePRIVATE_STYLES();
  const titleFontSize = getTheme().fonts.xxLarge.fontSize;
  const headerIconStyles: IIconStyles = {
    root: {
      fontSize: titleFontSize,
      width: titleFontSize,
      height: titleFontSize,
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.upperContent}>
        <h1 className={styles.title}>
          <label htmlFor={formService.title.id} aria-label={xlate('Title')}>
            <Icon iconName={AppRoutes.createPresentation.icon} styles={headerIconStyles} />
          </label>
          <BwjTextField
            id={formService.title.id}
            required={false}
            onUpdate={formService.title.onUpdate}
            input={formModel.title}
            filter={/.{0,50}/}
            placeholder={xlate('Discussion plan title')}
            styles={{ root: {
              fontSize: theme.fonts.xxLarge.fontSize,
              flexGrow: 1,
            },
            field: {
              fontSize: theme.fonts.xxLarge.fontSize,
              fontWeight: 600,
            }, 
            fieldGroup: {
              height: '2em'
            }}}
          />
        </h1>
        {props.showDisplayPanelButton &&
        <div className={styles.panelControl}>
          <PRIVATE_PANEL_DISPLAY_BUTTON
            title={xlate('Open discussion plan preview panel')}
            onClick={props.onOpenPreviewPanel}
          />
        </div>}
        <div className={styles.mobilePanelControl}>
          <PRIVATE_PANEL_DISPLAY_BUTTON
            title={xlate('Open discussion plan preview panel')}
            onClick={props.onOpenPreviewPanel}
          />
        </div>
      </div>
      <div className={styles.lowerContent}>
        <div className={styles.durationTray}>
          <label className={styles.durationLabel}>{xlate('Discussion duration:')}</label>

          {formModel.durationSpecifiable === false &&
          <span className={styles.fixedDurationValue}>{secondsToDurationString(formModel.specDuration)}</span>}

          {formModel.durationSpecifiable && formModel.presenterDefinedDuration.value === undefined &&
          <div className={styles.durationControlContainer}>
            <Icon aria-hidden iconName='WarningSolid' className={styles.warningIcon} />
            <span className={styles.fixedDurationValue}>{xlate('To be determined')}</span>
            <ActionButton
              iconProps={{ iconName: 'Edit'}}
              onClick={() => formService.presenterDefinedDuration.onUpdate(1)}
            >
              {xlate('Specify duration')}
            </ActionButton>
          </div>}

          {formModel.durationSpecifiable && formModel.presenterDefinedDuration.value !== undefined &&
          <div className={styles.durationControlContainer}>
            <DurationField
              id={formService.presenterDefinedDuration.id}
              required={false}
              minDuration={formModel.specDuration ?? 1}
              maxDuration={formService.MAX_DURATION}
              input={formModel.presenterDefinedDuration}
              onUpdate={onDurationFieldUpdate}
            />
            <ActionButton
              iconProps={{ iconName: 'Clear'}}
              onClick={() => formService.presenterDefinedDuration.onUpdate(undefined)}
            >
              {xlate('Clear duration')}
            </ActionButton>
          </div>}
        </div>
        <div className={styles.actionButtonTray}>
          <div className={styles.desktopOnlyAction}>
            <CommandButton
              iconProps={{ iconName: 'Storyboard' }}
              text={xlate('Editor layout')}
              menuProps={layoutMenuProps}
            />
          </div>
          {props.showDisplayPreviewHelpPanelButton ?
          <ActionButton
            iconProps={{ iconName: 'DocumentApproval'}}
            onClick={props.onDisplayPreview}
          >
            {xlate('Display preview')}
          </ActionButton>
          :
          <ActionButton
            iconProps={{ iconName: 'Help'}}
            onClick={props.onDisplayHelp}
          >
            {xlate('Display help')}
          </ActionButton>}
          {props.allowStartPresentationButton && props.saveDisabled &&
          <ActionButton
            iconProps={{ iconName: 'Play'}}
            onClick={props.onStartPresentation}
          >
            {xlate('Start discussion')}
          </ActionButton>}
          <ActionButton
            iconProps={{ iconName: 'Save'}}
            onClick={props.onSavePresentation}
            disabled={props.saveDisabled}
          >
            {xlate('Save plan')}
          </ActionButton>
        </div>
      </div>
    </header>
  );
};
